import {
  type AbstractTemplateInOut,
  type AnnulOperation,
  type BranchChangeset,
  isTemplateCreateOperation,
} from '@festina/configuration-general-types/configuration'
import { OperationLayerMap, type Tree } from '@festina-life/api'
import {
  assertNotEmpty,
  dateUtils,
  notEmpty,
  type Optional,
} from '@festina-life/core'

import type { ConfigurationItemsByBlock } from '../../workspace/operationsContext'
import { templateOperationFindFn } from '../helpers'
import { constructIndexedOperations } from '../indexedOperations/helpers'
import type { WorkspaceOperationOf } from '../types'
import {
  type IndexedTemplateOperations,
  type IndexedTemplateOperationWithMetaData,
  isTemplateOperation,
  type ShadowedOperationsPerTemplateIdAndDate,
  type TemplateOperation,
} from './types'

export const getTemplateOperationFromDate = (operation: TemplateOperation) => {
  if (isTemplateCreateOperation(operation)) {
    return operation.entity?.templateFromDate
  }
  return operation.fromDate
}

export const getTemplateOperationName = (operation: TemplateOperation) => {
  if (isTemplateCreateOperation(operation)) {
    return operation.entity?.name ?? '-'
  }
  return operation.name ?? '-'
}

export const getTemplateOperationAtDate = (
  indexedTemplateOperations: Optional<IndexedTemplateOperations>,
  atDate: Date,
) => {
  const operations = indexedTemplateOperations?.operations ?? []

  const comparableDate = atDate.getTime()

  return (operations ?? []).findLast((compareTo) => {
    const compareToFromDate = getTemplateOperationFromDate(
      compareTo.internalOperation,
    )
    return (
      !compareToFromDate ||
      dateUtils.dateUtcAsLocal(compareToFromDate).valueOf() <= comparableDate
    )
  })
}

export const constructIndexedTemplateOperations = ({
  configurationChangesets,
  draftTemplateOperations,
  draftAnnulOperations,
  blockTopology,
}: {
  configurationChangesets: Optional<BranchChangeset[]>
  draftTemplateOperations: Optional<WorkspaceOperationOf<TemplateOperation>[]>
  draftAnnulOperations?: WorkspaceOperationOf<AnnulOperation>[]
  blockTopology: Tree
}): ConfigurationItemsByBlock<IndexedTemplateOperations> =>
  constructIndexedOperations<TemplateOperation, IndexedTemplateOperations>({
    configurationChangesets,
    draftOperations: draftTemplateOperations,
    draftAnnulOperations,
    blockTopology,
    isIndexedOperation: isTemplateOperation,
    getEntityId: getTemplateOperationName,
    createDefault: (templateId) => ({
      templateId,
      // These fields are updated once all operations have been added.
      lastEntity: undefined,
      isDraft: false,
      operations: [],
    }),
    findIndexFn: templateOperationFindFn,
    postProcessEntity: (enrichedTemplate) => {
      enrichedTemplate.lastEntity = enrichedTemplate.operations
        .map((operation) => operation.internalOperation)
        .findLast(isTemplateCreateOperation)?.entity
    },
  })

export const getTemplateProperty = <T extends AbstractTemplateInOut>(
  template: T,
  propertyName: string,
) =>
  template.properties
    ?.filter(notEmpty)
    .find((property) => property.name === propertyName)

export const TemplatePathParts =
  /^\{(?<templateId>[\p{L}\d_\-.]+)\}(?<intermediatePath>.*)$/v

export const templateIdFromPath = (path: string) => {
  const templateId = TemplatePathParts.exec(path)?.groups?.['templateId']
  assertNotEmpty(templateId)

  return templateId
}

const getShadowedOperationsForTemplate = (
  indexedOperation: IndexedTemplateOperations,
) =>
  new Map(
    Array.from(
      indexedOperation.operations
        .reduce((acc, operation) => {
          const operationFromDate =
            getTemplateOperationFromDate(operation.internalOperation) ?? null

          if (!acc.get(operationFromDate)) {
            acc.set(operationFromDate, [])
          }

          acc.get(operationFromDate)?.push(operation)

          return acc
        }, new Map<string | null, IndexedTemplateOperationWithMetaData[]>())
        .entries(),
    ).filter(([, operations]) => operations.length > 1),
  )

/**
 *
 * @param allIndexedOperations
 * @return a Map indexed by Template ID, of all shadowed template create operations, indexed by templateFromDate:
 * results.get(templateId).get(fromDate) is an Array of conflicting/shadowed operations for a template on a given date.
 */
export const getAllShadowedTemplateCreateOperations = (
  allIndexedOperations: Map<string, IndexedTemplateOperations>,
): ShadowedOperationsPerTemplateIdAndDate =>
  new Map(
    Array.from(allIndexedOperations.entries())
      .map(([templateId, indexedOperation]) => {
        const shadowedOperationsByDate =
          getShadowedOperationsForTemplate(indexedOperation)
        return [
          templateId,
          shadowedOperationsByDate.size > 0
            ? shadowedOperationsByDate
            : undefined,
        ] as const
      })
      .filter(
        (
          entry,
        ): entry is [
          string,
          Map<string | null, IndexedTemplateOperationWithMetaData[]>,
        ] => notEmpty(entry.at(1)),
      ),
  )

export const filterShadowedOperationsForCondition = (
  allShadowedOperations: OperationLayerMap<ShadowedOperationsPerTemplateIdAndDate>,
  condition?: (operation: IndexedTemplateOperationWithMetaData) => boolean,
): OperationLayerMap<ShadowedOperationsPerTemplateIdAndDate> =>
  new OperationLayerMap(
    Array.from(allShadowedOperations.entries())
      .map(
        ([layer, shadowedOperationsForLayer]) =>
          [
            layer,
            new Map(
              Array.from(shadowedOperationsForLayer.entries())
                .map(([templateId, shadowedOperationsForTemplateByDate]) => {
                  const entriesForCondition = Array.from(
                    shadowedOperationsForTemplateByDate.entries(),
                  ).filter(([, operationsInConflictForDate]) =>
                    condition
                      ? operationsInConflictForDate.some((operation) =>
                          condition(operation),
                        )
                      : true,
                  )

                  return [
                    templateId,
                    entriesForCondition.length
                      ? new Map(entriesForCondition)
                      : undefined,
                  ] as const
                })
                .filter(
                  (
                    entries,
                  ): entries is [
                    string,
                    Map<string | null, IndexedTemplateOperationWithMetaData[]>,
                  ] => notEmpty(entries.at(1)),
                ),
            ),
          ] as const,
      )
      .filter(
        ([, shadowedOperationsForLayer]) => !!shadowedOperationsForLayer.size,
      ),
  )
